import { BLANK_IDENTIFICATION, PANEL_SELECTOR } from './customer-agent.consts'
import {
  cancelClosingAnimation,
  hasLoadBeenRequested,
  isChatInThePanel,
  markLoadRequested,
  mountPanel,
  playClosingAnimation,
} from './customer-agent.panel'
import type {
  ICustomerAgentIdentity,
  IHubSpotConversations,
  IHubSpotConversationsSettings,
  IHubSpotWidget,
  THubSpotTrackingCommand,
} from './customer-agent.types'

const withConversations = (command: (conversations: IHubSpotConversations) => void): void => {
  if (window.HubSpotConversations) {
    command(window.HubSpotConversations)
    return
  }

  const onReady = window.hsConversationsOnReady ?? []
  // HubSpot runs this queue again on every `widget.load()`, so a queued command would fire on each load.
  let hasRun = false
  onReady.push(() => {
    if (hasRun || !window.HubSpotConversations) return
    hasRun = true
    command(window.HubSpotConversations)
  })
  window.hsConversationsOnReady = onReady
}

const getSettings = (): IHubSpotConversationsSettings => {
  const settings = window.hsConversationsSettings ?? {}
  window.hsConversationsSettings = settings
  return settings
}

// Pushing before the tracking code loads is supported: `_hsq` is a queue by design.
const pushTracking = (...commands: THubSpotTrackingCommand[]): void => {
  const trackingQueue = window._hsq ?? []
  window._hsq = trackingQueue
  commands.forEach(command => trackingQueue.push(command))
}

const trackPageViewForIdentity = (): void => {
  pushTracking(['setPath', `${window.location.pathname}${window.location.search}`], ['trackPageView'])
}

// An inline-embedded widget has no collapsed form: it renders open in our panel and stays open until
// `remove()`. Set here rather than in the host's `index.html` because HubSpot reads it at `load()` time.
const embedInThePanel = (): void => {
  getSettings().inlineEmbedSelector = PANEL_SELECTOR
}

const removeWidget = (widget: IHubSpotWidget): void => {
  if (hasLoadBeenRequested()) widget.remove()
}

// `remove()`, not `close()`: closing collapses the widget to a launcher there is no setting to hide.
export const closeCustomerAgent = (): void => {
  withConversations(({ widget }) => {
    playClosingAnimation(() => removeWidget(widget))
  })
}

export const openCustomerAgent = (): void => {
  mountPanel(closeCustomerAgent)
  embedInThePanel()
  cancelClosingAnimation()

  withConversations(({ widget }) => {
    if (isChatInThePanel()) return

    // `load()` is throttled to one call per second, and the `remove()` below would kill a load already on
    // its way, so a click while one is in flight has to be a no-op.
    if (hasLoadBeenRequested() && widget.status().pending) return

    // A `load()` that ran while the panel was off the page leaves the widget stuck reporting `pending`,
    // and only `remove()` gets it out of that state.
    removeWidget(widget)
    widget.load()
    markLoadRequested()
  })
}

// Blanks the identity but leaves the widget alone, which is what an anonymous open needs —
// `endCustomerAgentSession` would also remove it.
export const clearCustomerAgentIdentity = (): void => {
  const settings = getSettings()
  settings.identificationEmail = BLANK_IDENTIFICATION
  settings.identificationToken = BLANK_IDENTIFICATION
}

// A sequence, not a call: `identify` only stores traits locally and the pageview is what transmits them.
export const identifyCustomerAgent = ({ email, identificationToken }: ICustomerAgentIdentity): void => {
  const settings = getSettings()
  settings.identificationEmail = email
  // Written unconditionally: on a re-identify without a token (workspace switch), leaving the
  // previous one in place would pair it with the new email.
  settings.identificationToken = identificationToken ?? BLANK_IDENTIFICATION

  pushTracking(['identify', { email }])
  trackPageViewForIdentity()

  withConversations(({ widget }) => {
    if (isChatInThePanel()) widget.refresh()
  })
}

// The identity has to go, or the next person to load the widget on this machine would be identified as
// this one. Blanking is synchronous, which is what a logout navigating away needs.
export const endCustomerAgentSession = (): void => {
  clearCustomerAgentIdentity()

  withConversations(({ widget }) => removeWidget(widget))
}
